import React from 'react';
import { formatCurrency } from 'Core/utils/numberFormat';
import VerticalRadioOption from 'Core/components/VerticalOption/VerticalRadioOption';
import { ImBlocked } from 'react-icons/im';
import offerTagConfig from 'Modules/financing/config/offerTagConfig';
import { useTranslation } from 'react-i18next';
import OfferOptionsHeader, { LEGACY_MONTHS_TO_PAY } from './OfferOptionsHeader';

const USE_DERIVED_TITLE = true;

export default function OfferOptions({
  offers,
  selectedOffer,
  setSelectedOffer,
  amount_to_pay,
  already_paid_amount,
  showOfferDetails,
}) {
  const { t } = useTranslation();

  // List of abbreviations to exclude from sentence splitting
  const abbreviationExclusions = ['Mo'];

  const splitSentences = (text) => {
    if (!text) return [];

    // Create a regex pattern that matches periods NOT preceded by abbreviations
    // This uses a negative lookbehind to exclude our abbreviations
    const abbreviationsPattern = abbreviationExclusions.join('|');
    const regex = new RegExp(`(?<!\\b(?:${abbreviationsPattern}))\\.\\s+`, 'g');

    const sentences = text
      .split(regex)
      .map((sentence) => sentence.trim())
      .filter((sentence) => sentence.length > 0);

    // Add back the period to each sentence (except if it already ends with one)
    return sentences.map((sentence) => {
      if (sentence.endsWith('.')) {
        return sentence;
      }
      return sentence + '.';
    });
  };
  const getEstimatedMonthlyPayment = (principle, num_of_months, rate, promoDays) => {
    // Legacy applications are quoted as a flat split of the full amount, with no interest.
    if (promoDays) {
      if (!amount_to_pay) return '';
      return formatCurrency(amount_to_pay / LEGACY_MONTHS_TO_PAY, { withDecimals: true });
    }
    if (rate && principle && num_of_months) {
      const monthly_rate = rate / 100 / 12;
      const monthly_rate_inc = monthly_rate + 1;
      const est_monthly_payment_min =
        (principle * monthly_rate * Math.pow(monthly_rate_inc, num_of_months)) /
        (Math.pow(monthly_rate_inc, num_of_months) - 1);
      return formatCurrency(est_monthly_payment_min, { withDecimals: true });
    }
    return '';
  };
  return offers.installment.map(({ program, terms, description, type }, idx) => {
    const [
      {
        id,
        amount_min,
        require_downpayment_percent,
        apr,
        term,
        waived_interest: { days: promoTermDays } = {},
      },
    ] = terms;
    const checked = selectedOffer.id === id;
    const isDisabled = amount_to_pay - already_paid_amount < amount_min;
    const emp_min = getEstimatedMonthlyPayment(amount_to_pay, term, apr, promoTermDays);

    const promoTerm = ((promoTermDays / 365) * 12).toFixed(0);
    const isPromo = promoTermDays > 0;

    const getDerivedTitle = () => {
      if (isPromo) {
        return `${term} mo. — no interest for ${promoTerm} mo.`;
      }
      return `${term} mo. at ${apr.toFixed(2)}% APR`;
    };
    const title = USE_DERIVED_TITLE ? getDerivedTitle() : program;
    const getTag = () => {
      if (offerTagConfig?.length > 0) {
        const tagObject = offerTagConfig.find((tagObj) => {
          return tagObj.offerIds.some((itemId) => id === itemId);
        });
        return tagObject?.tagLabel ? t(tagObject.tagLabel) : null;
      }
      return null;
    };

    return (
      <div
        className={`!flex-col-4 min-h-[15em] first:pl-0 last:pr-0 px-2 transition-all duration-300 ${
          checked ? 'scale-105 z-10' : 'scale-100'
        }`}
        key={id}
      >
        <VerticalRadioOption
          key={id}
          className={`mb-3 justify-between transition-all duration-300 ${
            checked
              ? 'ring-4 ring-blue-500 shadow-2xl shadow-blue-500/50 border-blue-500'
              : 'shadow-md hover:shadow-lg'
          }`}
          name="offer"
          value={id}
          checked={checked}
          disabled={isDisabled}
          data-testid={'select-offer-radio-' + idx}
          tag={getTag()}
          onChange={(e) => {
            setSelectedOffer({
              id: e.target.value,
              amount_min,
              require_downpayment_percent,
              type,
            });
          }}
        >
          <div className="text-center flex flex-col pb-2 justify-around radio-header">
            <h5 className="text-center font-semibold text-sm px-1">{title}</h5>
            {/* <OfferOptionsAmountBox empMin={emp_min} empMax={emp_max} isPromo={isPromo} aprMin={apr} aprMax={apr_max} /> */}
            <div className="font-extrabold text-2xl mb-2 py-2">{emp_min}</div>
            <div className="font-light text-sm mb-2">Estimated Monthly Payment</div>
            {!checked && isDisabled && (
              <div className="mb-4 mt-6 text-gray-400 flex items-center justify-center">
                <span className="inline-block">
                  <ImBlocked />
                </span>
                <span className="ml-2">Not Eligible</span>
              </div>
            )}
          </div>
          <OfferOptionsHeader
            isPromo={isPromo}
            checked={checked}
            promoTerm={promoTerm}
            amountToPay={amount_to_pay}
          />

          {showOfferDetails && (
            <div className="h-full px-4 radio-description text-sm flex flex-col justify-start">
              <ul className="list-none space-y-3 mt-4">
                {splitSentences(description?.replaceAll('<br />', ' ')).map((item, ind) => {
                  return (
                    <li key={ind} className="flex items-start text-justify text-xs">
                      <span className="inline-block mr-2 mt-0.5 text-blue-500">•</span>
                      <span>{item}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </VerticalRadioOption>
      </div>
    );
  });
}
